<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { createEventDispatcher } from 'svelte';

  export let placeholder: string;
  export let options: Array<{value: any, label: string}>;
  export let selectedValue: any = null;
  export let disabled: boolean = false;
  export let id: string;
  export let name: string;
  export let validateOnChange: boolean = false;
  export let onValidate: (() => void) | undefined = undefined;

  const dispatch = createEventDispatcher();

  let isOpen = false;
  let searchTerm = '';
  let filteredOptions: Array<{value: any, label: string}> = [];
  let dropdownElement: HTMLElement;
  let inputElement: HTMLInputElement;
  let listboxId = `listbox-${Math.random().toString(36).substr(2, 9)}`;

  $: {
    if (searchTerm) {
      filteredOptions = options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      filteredOptions = options;
    }
  }

  $: selectedOption = options.find(option => option.value === selectedValue);

  const toggleDropdown = () => {
    if (disabled) return;
    isOpen = !isOpen;
    if (isOpen && inputElement) {
      inputElement.focus();
    }
  };

  const selectOption = (option: {value: any, label: string}) => {
    selectedValue = option.value;
    searchTerm = '';
    isOpen = false;
    dispatch('change', { value: option.value });
    if (validateOnChange && onValidate) {
      onValidate();
    }
  };

  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    searchTerm = target.value;
    isOpen = true;
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      isOpen = false;
      searchTerm = '';
    } else if (event.key === 'Enter' && filteredOptions.length > 0) {
      event.preventDefault();
      selectOption(filteredOptions[0]);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
      isOpen = false;
      searchTerm = '';
    }
  };

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<div class="relative" bind:this={dropdownElement}>
  <div
    class="std-select mb-[-1px] font-MrEavesXLSanNarOT pr-[32px] relative text-[20px] px-[14px] w-full h-[42px] bg-grey border-solid border-transparent border-1 rounded-[8px] z-[10] outline-none cursor-pointer flex items-center"
    class:text-input={selectedValue !== null && selectedValue !== -1}
    class:text-gray-400={selectedValue === null || selectedValue === -1}
    on:click={toggleDropdown}
    on:keydown={(e) => e.key === 'Enter' ? toggleDropdown() : null}
    role="combobox"
    aria-expanded={isOpen}
    aria-haspopup="listbox"
    aria-controls={listboxId}
    tabindex="0"
    {id}
  >
    <input
      bind:this={inputElement}
      type="text"
      class="w-full bg-transparent border-none outline-none"
      class:text-input={selectedValue !== null && selectedValue !== -1}
      class:text-gray-400={selectedValue === null || selectedValue === -1}
      placeholder={placeholder}
      value={isOpen ? searchTerm : (selectedOption ? selectedOption.label : '')}
      on:input={handleInput}
      on:keydown={handleKeydown}
      {disabled}
      readonly={!isOpen}
    />
    <div class="absolute right-[12px] top-1/2 transform -translate-y-1/2 pointer-events-none">
      <svg 
        class="w-4 h-4 transition-transform duration-200" 
        class:rotate-180={isOpen}
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 335.877 196.929"
      >
        <path d="M306.885 0 167.938 138.947 28.991 0 0 28.991l167.938 167.938L335.877 28.991 306.885 0z" fill="currentColor" />
      </svg>
    </div>
  </div>

  {#if isOpen}
    <div 
      class="absolute top-full left-0 w-full bg-grey border border-transparent border-1 rounded-[8px] shadow-lg z-[20] max-h-[200px] overflow-y-auto min-w-0"
      role="listbox"
      id={listboxId}
    >
      {#each filteredOptions as option, index}
        <div
          class="px-[14px] py-[10px] text-[20px] cursor-pointer hover:bg-gray-600 font-MrEavesXLSanNarOT text-white {selectedValue === option.value ? 'bg-gray-700' : ''}"
          on:click={() => selectOption(option)}
          on:keydown={(e) => e.key === 'Enter' && selectOption(option)}
          role="option"
          aria-selected={selectedValue === option.value}
          tabindex="0"
        >
          {@html option.label}
        </div>
      {/each}
      {#if filteredOptions.length === 0}
        <div class="px-[14px] py-[10px] text-[20px] text-white font-MrEavesXLSanNarOT">
          No locations found
        </div>
      {/if}
    </div>
  {/if}

  <!-- Hidden input for form submission -->
  <input type="hidden" {name} value={selectedValue || ''} />
</div>
